/**
 * Backup e restauração (segurança) — só o Dono. O backup é cifrado com a senha de
 * login e BAIXADO pelo navegador (fica fora do servidor). Restaurar SUBSTITUI os
 * dados atuais — sempre com alerta grande e confirmação.
 * 0.1.85: saiu o "salvar numa pasta do computador" (versão desktop).
 */
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle, CheckCircle2, Download, HardDriveDownload, Loader2, RefreshCw, Upload,
} from "lucide-react";
import { useState } from "react";
import { api, ApiError, baixarArquivoPost } from "../lib/api";

const erroDe = (e: unknown) =>
  e instanceof ApiError
    ? ((e.body as { detail?: string })?.detail ?? e.message)
    : "Não foi possível concluir.";

export function BackupCard() {
  // Backup
  const [senhaBkp, setSenhaBkp] = useState("");
  // Gera e BAIXA o .sindicobak no navegador.
  const baixar = useMutation({
    mutationFn: () =>
      baixarArquivoPost("/lgpd/backup/baixar", { senha: senhaBkp }, "SindicoApp-Backup.sindicobak"),
    onSuccess: () => setSenhaBkp(""),
  });

  // Restaurar (escolhe o arquivo com "Buscar", como o PDF do mês)
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [senhaRes, setSenhaRes] = useState("");
  const [modal, setModal] = useState(false);
  const [ciente, setCiente] = useState(false);
  const [resOk, setResOk] = useState(false);
  const restaurar = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("arquivo", arquivo as File);
      fd.append("senha", senhaRes);
      return api.postForm<{ precisa_reiniciar: boolean }>("/lgpd/backup/restaurar-arquivo", fd);
    },
    onSuccess: () => setResOk(true),
  });

  const inputCls = "w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm";

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-4">
      <div className="flex items-center gap-2">
        <HardDriveDownload size={17} className="text-emerald-600" />
        <h4 className="text-sm font-semibold text-slate-900">Backup e restauração</h4>
      </div>

      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900 flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
        <span>
          Guarde o backup <b>fora do servidor</b> (pendrive, HD externo ou nuvem). O servidor já
          faz o backup automático diário; este é a sua cópia extra. O arquivo é
          <b> cifrado com a sua senha de login</b>.
        </span>
      </div>

      {/* Fazer backup */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Download size={15} className="text-slate-500" />
          <span className="text-[13px] font-medium text-slate-800">Fazer backup</span>
        </div>
        {(
          <div className="space-y-1.5">
            <p className="text-[12px] text-slate-500">
              Digite sua senha e clique em <b>Baixar backup</b>. O arquivo é baixado pelo
              navegador — escolha a pasta na <b>janela de download</b> (ou procure em
              <b> Downloads</b>) e guarde num pendrive/HD.
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <input type="password" value={senhaBkp} onChange={(e) => setSenhaBkp(e.target.value)}
                placeholder="Sua senha (de login)" className={`${inputCls} w-56`} />
              <button type="button" onClick={() => baixar.mutate()} disabled={!senhaBkp || baixar.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-40 text-white text-sm font-medium rounded-md">
                {baixar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Baixar backup
              </button>
              {baixar.isSuccess && (
                <span className="text-[12px] text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 size={14} /> Download iniciado — guarde o arquivo num pendrive/HD.
                </span>
              )}
            </div>
          </div>
        )}
        {baixar.error && <p className="text-xs text-rose-700 mt-1">{erroDe(baixar.error)}</p>}
      </div>

      {/* Restaurar */}
      <div className="border-t border-slate-100 pt-3">
        <div className="flex items-center gap-2 mb-1">
          <Upload size={15} className="text-rose-500" />
          <span className="text-[13px] font-medium text-slate-800">Restaurar backup</span>
        </div>
        <p className="text-[11px] text-slate-500 mb-2">
          Substitui os dados atuais pelos do backup. Antes de restaurar, o app salva
          automaticamente o estado atual (para poder desfazer).
        </p>
        {resOk ? (
          <div className="text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2">
            <RefreshCw size={15} className="mt-0.5" />
            <div>
              Backup restaurado. Os dados entram em vigor quando o servidor <b>reiniciar</b>:
              rode <b>Reiniciar-Servidor.bat</b> (pasta Servidor-Caminho-A\scripts) e depois
              recarregue a página (Ctrl+F5).
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-500 mb-1">
                Arquivo de backup (.sindicobak)
              </span>
              <input type="file" accept=".sindicobak"
                onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                className={inputCls} />
              {arquivo && (
                <span className="block text-[11px] text-slate-500 mt-1 break-all">
                  Selecionado: <b>{arquivo.name}</b>
                </span>
              )}
            </label>
            <div className="flex flex-wrap items-end gap-2">
              <input type="password" value={senhaRes} onChange={(e) => setSenhaRes(e.target.value)}
                placeholder="Senha usada no backup" className={`${inputCls} w-56`} />
              <button type="button" onClick={() => { setCiente(false); setModal(true); }} disabled={!arquivo || !senhaRes}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-40 text-sm font-medium rounded-md">
                <Upload size={14} /> Restaurar…
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmação da restauração */}
      {modal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="bg-rose-600 text-white px-5 py-4 flex items-center gap-3">
              <AlertTriangle size={22} />
              <h3 className="text-base font-semibold">Restaurar vai substituir tudo</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-700">
                Todos os dados atuais serão <b className="text-rose-700">substituídos</b> pelos
                do backup. O que está agora será removido (mas salvamos um backup do estado
                atual antes, por segurança).
              </p>
              <label className="flex items-start gap-2 text-[13px] text-slate-700">
                <input type="checkbox" checked={ciente} onChange={(e) => setCiente(e.target.checked)} className="mt-0.5" />
                Entendo que os dados atuais serão substituídos.
              </label>
              {restaurar.error && <p className="text-xs text-rose-700">{erroDe(restaurar.error)}</p>}
              <div className="flex gap-2 justify-end pt-1">
                <button type="button" onClick={() => setModal(false)} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">
                  Cancelar
                </button>
                <button type="button" onClick={() => restaurar.mutate()} disabled={!ciente || restaurar.isPending}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white text-sm font-semibold rounded-md">
                  {restaurar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                  Restaurar mesmo assim
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
